import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Eye, Save, Send, History, Loader2 } from "lucide-react";
import { getLegalDoc, getLegalVersion, saveLegalDraft, publishLegal, fmtDate } from "@/lib/legalApi";
import { errMsg } from "@/lib/applyApi";
import { RichTextEditor } from "@/components/legal/RichTextEditor";
import { PolicyBody } from "@/components/legal/PolicyBody";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtMT } from "@/lib/mountainTime";

// Add future documents (Terms of Use, Cookie Policy…) here and in backend legal_docs.DOC_TYPES.
const DOCS = [["privacy_policy", "Privacy Policy"]];

function DocDialog({ title, sub, html, onClose, footer, testId }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testId} className="w-[calc(100%-1rem)] max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl p-4 sm:p-6">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{sub}</DialogDescription></DialogHeader>
        <PolicyBody html={html} testId={`${testId}-body`} />
        {footer && <DialogFooter className="gap-2">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

function Versions({ d, onView }) {
  if (!d.versions.length) return <p data-testid="legal-no-versions" className="text-sm text-stone-500">No version has been published yet.</p>;
  return (
    <ul data-testid="legal-versions" className="divide-y divide-stone-100 text-sm">
      {d.versions.map((v) => (
        <li key={v.id} data-testid={`legal-version-${v.version}`} className="py-2 flex items-center justify-between gap-2">
          <span><strong>Version {v.version}</strong>{d.current?.id === v.id && <span className="ml-2 text-[11px] font-semibold uppercase text-emerald-700">Published</span>}
            <span className="block text-xs text-stone-500">Effective {fmtDate(v.effective_date)} · published {fmtMT(v.published_at)} by {v.published_by}</span></span>
          <Button variant="outline" data-testid={`legal-view-${v.version}`} onClick={() => onView(v)} className="h-10 rounded-full shrink-0">View</Button>
        </li>
      ))}
    </ul>
  );
}

function DocEditor({ type, title }) {
  const [d, setD] = useState(null);
  const [html, setHtml] = useState("");
  const [eff, setEff] = useState("");
  const [busy, setBusy] = useState("");
  const [modal, setModal] = useState(null);
  const load = (x) => { setD(x); setHtml(x.draft.html); setEff(x.draft.effective_date); };
  useEffect(() => { getLegalDoc(type).then(load).catch((e) => toast.error(errMsg(e, "Could not load"))); }, [type]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-stone-400" />;
  const dirty = html !== d.draft.html || eff !== d.draft.effective_date;
  const run = async (k, fn, ok) => { setBusy(k); try { load(await fn()); toast.success(ok); return true; } catch (e) { toast.error(errMsg(e, "Could not save")); return false; } finally { setBusy(""); } };
  const save = () => run("save", () => saveLegalDraft(type, { html, effective_date: eff }), "Draft saved");
  const publish = async (kind) => {
    if (dirty && !(await save())) return;
    if (await run("publish", () => publishLegal(type, kind), `${title} version ${d.next_versions[kind]} published`)) setModal(null);
  };
  const view = async (v) => setModal({ kind: "version", v: await getLegalVersion(type, v.id) });
  return (
    <div data-testid={`legal-doc-${type}`} className="space-y-4">
      <div data-testid="legal-current" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm">
        {d.current ? <>Published: <strong data-testid="legal-current-version">Version {d.current.version}</strong> · Effective <strong data-testid="legal-current-effective">{fmtDate(d.current.effective_date)}</strong>
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="legal-public-link" className="ml-2 text-amber-800 underline">View public page</a></>
          : <span data-testid="legal-not-published">Not published yet. The public page shows a “being updated” notice until you publish.</span>}
      </div>
      <div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
          <h3 className="font-semibold text-stone-900">Draft</h3>
          <label className="text-xs font-semibold text-stone-600">Effective date
            <Input type="date" data-testid="legal-effective-date" value={eff} onChange={(e) => setEff(e.target.value)} className="mt-1 h-11 w-44 text-base" /></label>
        </div>
        <RichTextEditor key={d.draft.updated_at} value={html} onChange={setHtml} />
        <p className="mt-1 text-xs text-stone-500">{dirty ? "Unsaved changes" : `Draft saved ${d.draft.updated_at ? fmtMT(d.draft.updated_at) : ""} by ${d.draft.updated_by || "—"}`}. Drafts are never shown publicly.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button variant="outline" data-testid="legal-save-draft" disabled={!!busy || !dirty} onClick={save} className="h-11 rounded-full">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save Draft</>}</Button>
        <Button variant="outline" data-testid="legal-preview" onClick={() => setModal({ kind: "preview" })} className="h-11 rounded-full"><Eye className="h-4 w-4 mr-1.5" />Preview</Button>
        <Button data-testid="legal-publish" disabled={!!busy} onClick={() => setModal({ kind: "publish" })} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white"><Send className="h-4 w-4 mr-1.5" />Publish New Version</Button>
      </div>
      <div><h3 className="font-semibold text-stone-900 mb-1 flex items-center gap-1.5"><History className="h-4 w-4 text-amber-800" />Published Versions</h3><Versions d={d} onView={view} /></div>
      {modal?.kind === "preview" && <DocDialog testId="legal-preview-dialog" title={`${title} — Draft Preview`} sub={`Effective ${fmtDate(eff)} · not yet published`} html={html} onClose={() => setModal(null)} />}
      {modal?.kind === "version" && <DocDialog testId="legal-version-dialog" title={`${title} — Version ${modal.v.version}`} sub={`Effective ${fmtDate(modal.v.effective_date)} · published ${fmtMT(modal.v.published_at)} by ${modal.v.published_by}`} html={modal.v.html} onClose={() => setModal(null)} />}
      {modal?.kind === "publish" && (
        <DocDialog testId="legal-publish-dialog" title={`Publish ${title}`} sub={`This replaces the public ${title} with the draft below (effective ${fmtDate(eff)}). Earlier versions and sellers' past acceptances are kept.`} html={html} onClose={() => setModal(null)}
          footer={d.current ? <>
            <Button variant="outline" data-testid="legal-publish-minor" disabled={!!busy} onClick={() => publish("minor")} className="h-11 rounded-full">Minor update → {d.next_versions.minor}</Button>
            <Button data-testid="legal-publish-major" disabled={!!busy} onClick={() => publish("major")} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Major update → {d.next_versions.major}</Button>
          </> : <Button data-testid="legal-publish-confirm" disabled={!!busy} onClick={() => publish("minor")} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Publish Version 1.0</Button>} />
      )}
    </div>
  );
}

export function LegalDocuments() {
  const [open, setOpen] = useState(DOCS[0][0]);
  return (
    <section data-testid="legal-documents" className="rounded-xl border border-stone-200 bg-white p-5">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2"><Scale className="h-4 w-4 text-amber-800" /> Legal Documents</h2>
      <p className="text-xs text-stone-500 mt-1 mb-4">Edit, preview and publish versioned legal documents. Only administrators can make changes.</p>
      <div className="flex gap-2 mb-4">
        {DOCS.map(([k, l]) => <button key={k} type="button" data-testid={`legal-tab-${k}`} onClick={() => setOpen(k)} className={`h-10 px-4 rounded-full text-sm font-semibold ${open === k ? "bg-amber-800 text-white" : "bg-stone-100 text-stone-700"}`}>{l}</button>)}
      </div>
      {DOCS.filter(([k]) => k === open).map(([k, l]) => <DocEditor key={k} type={k} title={l} />)}
    </section>
  );
}
